import { useState, useEffect } from 'react';
import {
  fetchReviews,
  deleteReview,
  moderateReview,
  type ReviewFilters,
} from '@/api/marketplace-reviews.api';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  CheckCircle,
  XCircle,
  Trash2,
  Star,
  Flag,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function MarketplaceReviews() {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ReviewFilters>({
    limit: 20,
  });
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const loadReviews = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentFilters = loadMore ? { ...filters, cursor: nextCursor } : filters;
      const data = await fetchReviews(currentFilters);
      if (loadMore) {
        setReviews((prev) => [...prev, ...(data?.data || [])]);
      } else {
        setReviews(data?.data || []);
      }
      setNextCursor(data?.nextCursor || null);
      setHasMore(data?.hasMore || false);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, [filters]);

  const openDeleteDialog = (review: any) => {
    setSelectedReview(review);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedReview) return;
    try {
      await deleteReview(selectedReview._id, deleteReason);
      setReviews((prev) => prev.filter((r) => r._id !== selectedReview._id));
      setDeleteDialogOpen(false);
      setSelectedReview(null);
      setDeleteReason('');
    } catch (error) {
      console.error('Failed to delete review:', error);
    }
  };

  const handleModerate = async (reviewId: string, action: 'approve' | 'remove') => {
    try {
      await moderateReview(reviewId, action);
      loadReviews();
    } catch (error) {
      console.error('Failed to moderate review:', error);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm text-muted-foreground">{rating}</span>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6 h-full overflow-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketplace Reviews</h1>
          <p className="text-muted-foreground">
            View and moderate all marketplace reviews
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter reviews by status</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Select
            value={filters.isPublished ?? 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, isPublished: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Published" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Published</SelectItem>
              <SelectItem value="false">Unpublished</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.isFlagged ?? 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, isFlagged: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Flagged" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Flagged</SelectItem>
              <SelectItem value="false">Not Flagged</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Reviews Table */}
      <Card>
        <CardHeader>
          <CardTitle>Reviews</CardTitle>
          <CardDescription>
            {reviews.length} review(s) found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && reviews.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No reviews found
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reviewer</TableHead>
                  <TableHead>Reviewee</TableHead>
                  <TableHead>Listing</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Comment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow key={review._id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={review.reviewer?.profile || '/avatar.png'}
                          alt={review.reviewer?.username}
                          className="w-6 h-6 rounded-full"
                        />
                        <div>
                          <div className="text-sm font-medium">{review.reviewer?.username || '-'}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {review.reviewerRole}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={review.reviewee?.profile || '/avatar.png'}
                          alt={review.reviewee?.username}
                          className="w-6 h-6 rounded-full"
                        />
                        {review.reviewee?.username || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[150px]">
                      <div className="font-medium line-clamp-1">
                        {review.listing?.title || 'Deleted listing'}
                      </div>
                    </TableCell>
                    <TableCell>{renderStars(review.rating)}</TableCell>
                    <TableCell className="max-w-[200px]">
                      <div className="line-clamp-2 text-sm">
                        {review.comment || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {review.isPublished ? (
                          <Badge variant="default" className="flex items-center gap-1 w-fit">
                            <Eye className="w-3 h-3" />
                            Published
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                            <EyeOff className="w-3 h-3" />
                            Hidden
                          </Badge>
                        )}
                        {review.isFlagged && (
                          <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                            <Flag className="w-3 h-3" />
                            Flagged
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(review.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {review.isFlagged && review.isPublished && (
                          <Button
                            size="sm"
                            variant="default"
                            title="Approve (unflag)"
                            onClick={() => handleModerate(review._id, 'approve')}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {!review.isPublished && (
                          <Button
                            size="sm"
                            variant="default"
                            title="Publish"
                            onClick={() => handleModerate(review._id, 'approve')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          title="Delete review"
                          onClick={() => openDeleteDialog(review)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
          {hasMore && !loading && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => loadReviews(true)}>
                Load More
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Delete Review</DialogTitle>
            <DialogDescription>
              Remove this review. This will unpublish the review and update the user's rating stats.
            </DialogDescription>
          </DialogHeader>

          {selectedReview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Reviewer</Label>
                  <div className="font-medium">{selectedReview.reviewer?.username || '-'}</div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Reviewee</Label>
                  <div className="font-medium">{selectedReview.reviewee?.username || '-'}</div>
                </div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Rating</Label>
                <div>{renderStars(selectedReview.rating)}</div>
              </div>
              {selectedReview.comment && (
                <div>
                  <Label className="text-sm text-muted-foreground">Comment</Label>
                  <div className="text-sm">{selectedReview.comment}</div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="deleteReason">Reason *</Label>
                <Textarea
                  id="deleteReason"
                  placeholder="Explain why this review is being removed..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={!deleteReason.trim()}
              variant="destructive"
            >
              Delete Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
